import { ClientQueryStatistics, ClientQueryStatusEnum } from './client-query';
import { InternalCommentStatistics, InternalCommentStatusEnum } from './internal-comment';
import { WorkpaperProcedureStatusEnum, ProcedureStatistics } from './workpaper-procedure';
import { OtherScheduleStatistics, OtherScheduleStatusEnum } from './other-schedule';
import { UserOverview } from './user';
import { WorksheetStatistics, WorksheetStatusEnum } from './worksheet';
import { JournalStatistics } from './journal';
import { COASyncReport } from './entity';

export type WorkpaperPermissions = {
    preparer: Record<string, boolean>;
    reviewer: Record<string, boolean>;
    approver: Record<string, boolean>;
    guest: Record<string, boolean>;
};

export enum WorkpaperTypeEnum {
    ANNUAL_COMPLIANCE = 'ANNUAL_COMPLIANCE',
    INDIVIDUAL_TAX_RETURN = 'INDIVIDUAL_TAX_RETURN',
}

export enum WorkpaperSourceEnum {
    XERO = 'XERO',
    CSV = 'CSV',
}
export enum WorkpaperStatusEnum {
    ACTIVE = 'ACTIVE',
    ARCHIVED = 'ARCHIVED',
    COMPLETE = 'COMPLETE',
}
export enum WorkpaperRoleEnum {
    PREPARER = 'PREPARER',
    REVIEWER = 'REVIEWER',
    APPROVER = 'APPROVER',
    GUEST = 'GUEST',
}

export enum TrialBalanceAccountingBasisEnum {
    CASH = 'CASH',
    ACCRUAL = 'ACCRUAL',
}

export type ProcedureProgressBreakdown = {
    totalItems: number;
    percentComplete: number;
    fields: { label: WorkpaperProcedureStatusEnum; count: number; percent: number }[];
};

export type WorksheetProgressBreakdown = {
    totalItems: number;
    percentComplete: number;
    fields: { label: WorksheetStatusEnum; count: number; percent: number }[];
};

export type OtherScheduleProgressBreakdown = {
    totalItems: number;
    percentComplete: number;
    fields: { label: OtherScheduleStatusEnum; count: number; percent: number }[];
};

export type InternalCommentProgressBreakdown = {
    totalItems: number;
    percentComplete: number;
    fields: { label: InternalCommentStatusEnum; count: number; percent: number }[];
};

export type ClientQueryProgressBreakdown = {
    totalItems: number;
    percentComplete: number;
    fields: { label: ClientQueryStatusEnum; count: number; percent: number }[];
};

export type WorkpaperGuest = {
    id?: string;
    firstName?: string;
    lastName?: string;
    profileHexColour?: string;
    avatarUrl?: string | null | undefined;
    workpaperId?: string;
    firmId?: string;
    email: string;
};

export type WorkpaperAssignee = {
    id?: string;
    firstName?: string;
    lastName?: string;
    profileHexColour?: string;
    avatarUrl?: string | null;
    email: string;
    role: WorkpaperRoleEnum;
};

export type Workpaper = {
    id: string;
    entity: { name: string; id: string; avatarHexColour?: string; financialYearEndMonth: number; financialYearEndDay: number };
    groups?: { name: string; id: string }[];
    procedureTemplateId: string;
    checklistId: string;
    provider: WorkpaperSourceEnum;
    status: WorkpaperStatusEnum;
    accountingBasis?: TrialBalanceAccountingBasisEnum;
    name: string;
    periodEnd: string;
    dueDate: string;
    private: boolean;
    createdAt: Date;
    updatedAt: Date;
    createdBy: string;
    assigned: (UserOverview & { email: string; role: WorkpaperRoleEnum; certified: boolean })[];
    pendingExternalUsers?: { email: string; role: WorkpaperRoleEnum }[];
    completedAt: Date;
    completedBy: string;
    progressPercent: number;
    tasks?: number;
    materialityLimit?: { amount: number; isPercentage: boolean } | null;
    journalRefreshedAt?: Date;
    trialBalanceRefreshedAt?: Date;
    integrations: {
        fyi?: { job: { id: string; name: string; createdAt: Date; updatedAt: Date; documentsLastSynced: Date } };
    } | null;
    overdueDays?: number;
    overdue?: boolean;
    completedInDays?: number | null;
    type?: WorkpaperTypeEnum;
    permissions: WorkpaperPermissions;
    priorWorkpaperId?: string;
};

export type WorkpaperCertificationTask = {
    task: string;
    complete: boolean;
};

export type WorkpaperCertification = {
    preparer?: {
        certifiedOn?: Date | null;
        certifiedBy?: UserOverview | null;
        tasks?: WorkpaperCertificationTask[];
    };
    reviewer?: {
        certifiedOn?: Date | null;
        certifiedBy?: UserOverview | null;
        tasks?: WorkpaperCertificationTask[];
    };
    approver?: {
        certifiedOn?: Date | null;
        certifiedBy?: UserOverview | null;
        tasks?: WorkpaperCertificationTask[];
    };
};

export type WorkpaperProgress = {
    totalWorkpaperTasks?: number;
    start: Date;
    dueDate: Date;
    endDate: Date; // Due date or current date or completion date
    tasksComplete: {
        date: Date;
        total: number;
        complete: number | null;
    }[];
    duration: string;
    progressBreakdown?: {
        procedures: ProcedureStatistics;
        journals: JournalStatistics;
        worksheets: WorksheetStatistics;
        otherSchedules: WorksheetStatistics;
        internalComments: InternalCommentStatistics;
        clientQueries: ClientQueryStatistics;
    };
};

export enum WorkpaperExportEnum {
    NOT_EXPORTED = 'NOT_EXPORTED',
    EXPORTED = 'EXPORTED',
    EXPORTING = 'EXPORTING',
    ERROR = 'ERROR',
}

export type WorkpaperExportConfig = {
    procedures: boolean;
    trialBalance: { show: boolean; excluded: boolean };
    worksheets: {
        show: boolean;
        checklists: boolean;
        journals: { show: boolean; draft: boolean; posted: boolean; voided: boolean; archived: boolean };
        files: boolean;
        comments: boolean;
        queries: boolean;
    };
    otherSchedules: { show: boolean; files: boolean; comments: boolean; queries: boolean };
    journals: { show: boolean; draft: boolean; posted: boolean; voided: boolean; archived: boolean };
    files: boolean;
};

export type WorkpaperExport = {
    id: string;
    workpaperId: string;
    s3Key?: string;
    status: WorkpaperExportEnum;
    exportedAt?: Date | null;
    exportedBy?: UserOverview | null;
    exportErrorReason?: { message: string } | null;
    config: WorkpaperExportConfig;
};
export enum WorkpaperCompleteStatusEnum {
    COMPLETING = 'COMPLETING',
    COMPLETE_SUCCESS = 'COMPLETE_SUCCESS',
    COMPLETE_ERROR = 'COMPLETE_ERROR',
}

export type WorkpaperCompleteResult = {
    workpaperId: string;
    status: WorkpaperCompleteStatusEnum;
    completeError: { message: string };
    createdAt: Date;
    updatedAt: Date;
};

export type ManualTrialBalanceRow = {
    code: string;
    debitTY: number | null;
    creditTY: number | null;
    debitLY: number | null;
    creditLY: number | null;
};

export type TrialBalanceSyncReport = {
    hasChanged: boolean;
    createdCount: number;
    updatedCount: number;
    excludedCount: number;
    deletedCount: number;
    errors: string[];
    details: {
        created: Array<{ id: string; code: string | null; name: string }>;
        updated: Array<{ id: string; code: string | null; name: string }>;
        excluded: Array<{ id: string; code: string | null; name: string }>;
        deleted: Array<{ id: string; code: string | null; name: string }>;
    };
    chartResults?: COASyncReport;
};
